import React, { useState, useEffect } from 'react';
import { CardSkeleton } from '../../../components/enterprise';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Switch,
  Button,
  Stack,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CleaningServices as CleanupIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNotification } from '../../../contexts/NotificationContext';
import { toServerDate } from '../../../timezone';

const API_BASE = '/api/v1.0/settings/retention';

const RetentionSettingsPage = ({ orgId = null }) => {
  const notification = useNotification();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState([]);
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupComponent, setCleanupComponent] = useState(null);
  const [cleaningUp, setCleaningUp] = useState({});

  // Determine API path based on whether this is per-org or default tenant
  const apiPath = orgId ? `${API_BASE}/org/${orgId}` : API_BASE;
  const bulkApiPath = orgId ? `${API_BASE}/org/${orgId}/bulk` : `${API_BASE}/bulk`;

  // Fetch settings on mount or when orgId changes
  useEffect(() => {
    fetchSettings();
  }, [orgId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(apiPath);
      if (response.data?.success) {
        const data = response.data.data || [];
        setSettings(data);
        setOriginalSettings(JSON.parse(JSON.stringify(data)));
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Failed to fetch retention settings:', error);
      notification.error('Failed to load retention settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRetentionDaysChange = (component, value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 1 || numValue > 365) return;

    setSettings(prev =>
      prev.map(s =>
        s.component === component
          ? { ...s, retentionDays: numValue }
          : s
      )
    );
    setHasChanges(true);
  };

  const handleEnabledChange = (component, enabled) => {
    setSettings(prev =>
      prev.map(s =>
        s.component === component
          ? { ...s, enabled }
          : s
      )
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build bulk update request with only changed settings
      const requests = settings
        .filter((s, idx) => {
          const orig = originalSettings[idx];
          return s.retentionDays !== orig.retentionDays || s.enabled !== orig.enabled;
        })
        .map(s => ({
          component: s.component,
          retentionDays: s.retentionDays,
          enabled: s.enabled,
        }));

      if (requests.length === 0) {
        notification.info('No changes to save');
        return;
      }

      const response = await axios.put(bulkApiPath, requests);
      if (response.data?.success) {
        notification.success('Retention settings saved successfully');
        setOriginalSettings(JSON.parse(JSON.stringify(settings)));
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Failed to save retention settings:', error);
      notification.error('Failed to save retention settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCleanupClick = (component) => {
    setCleanupComponent(component);
    setCleanupDialogOpen(true);
  };

  const handleCleanupConfirm = async () => {
    if (!cleanupComponent) return;

    setCleanupDialogOpen(false);
    setCleaningUp(prev => ({ ...prev, [cleanupComponent]: true }));

    try {
      // When an operator is viewing a specific org, target that org explicitly —
      // otherwise the backend would derive the org from the operator's own
      // TenantContext and purge the wrong tenant's data.
      const cleanupPath = orgId
        ? `${API_BASE}/org/${orgId}/${cleanupComponent}/cleanup`
        : `${API_BASE}/${cleanupComponent}/cleanup`;
      const response = await axios.post(cleanupPath);
      if (response.data?.success) {
        const deleted = response.data?.data?.deletedCount || 0;
        notification.success(`Cleanup completed for ${cleanupComponent}: ${deleted} rows deleted`);
        fetchSettings();
      }
    } catch (error) {
      console.error('Failed to trigger cleanup:', error);
      notification.error('Failed to trigger cleanup');
    } finally {
      setCleaningUp(prev => ({ ...prev, [cleanupComponent]: false }));
      setCleanupComponent(null);
    }
  };

  const handleCleanupAll = async () => {
    setCleanupDialogOpen(false);
    setCleanupComponent(null);

    // Set all as cleaning
    const cleaningState = {};
    settings.forEach(s => {
      cleaningState[s.component] = true;
    });
    setCleaningUp(cleaningState);

    try {
      const response = await axios.post(`${API_BASE}/cleanup-all`);
      if (response.data?.success) {
        const total = response.data?.data?.totalDeleted || 0;
        notification.success(`Cleanup completed: ${total} total rows deleted`);
        fetchSettings();
      }
    } catch (error) {
      console.error('Failed to trigger cleanup:', error);
      notification.error('Failed to trigger cleanup');
    } finally {
      setCleaningUp({});
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return toServerDate(dateString)?.toLocaleString() ?? dateString;
  };

  const getComponentIcon = (component) => {
    const icons = {
      LOGS: '📝',
      TRACES: '🔍',
      METRICS: '📈',
      ALERTS: '🔔',
      SYNTHETICS: '🧪',
      RUM_SESSIONS: '👥',
      RUM_EVENTS: '📊',
      RUM_ERRORS: '❌',
      RUM_REPLAYS: '🎬',
      WORKFLOW_EXECUTIONS: '⚙️',
      KUBERNETES: '☸️',
    };
    return icons[component] || '📁';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Box sx={{ p: 3 }}><CardSkeleton count={2} /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Data Retention Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure how long different types of data are retained in the system.
                Data older than the retention period will be automatically deleted.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchSettings}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Stack>
          </Stack>

          {hasChanges && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You have unsaved changes. Click "Save Changes" to apply them.
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Component</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>Retention (Days)</TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>Enabled</TableCell>
                  <TableCell align="center">Last Cleanup</TableCell>
                  <TableCell align="center">Deleted</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settings.map((setting) => (
                  <TableRow key={setting.component} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>{getComponentIcon(setting.component)}</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {setting.componentDisplayName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {setting.componentDescription}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={setting.retentionDays}
                        onChange={(e) => handleRetentionDaysChange(setting.component, e.target.value)}
                        inputProps={{ min: 1, max: 365, style: { textAlign: 'center' } }}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={setting.enabled}
                        onChange={(e) => handleEnabledChange(setting.component, e.target.checked)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(setting.lastCleanupAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {setting.lastCleanupCount != null ? (
                        <Chip
                          label={setting.lastCleanupCount.toLocaleString()}
                          size="small"
                          color={setting.lastCleanupCount > 0 ? 'success' : 'default'}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Run cleanup now">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleCleanupClick(setting.component)}
                            disabled={!setting.enabled || cleaningUp[setting.component]}
                          >
                            {cleaningUp[setting.component] ? (
                              <CircularProgress size={20} />
                            ) : (
                              <CleanupIcon />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <InfoIcon fontSize="small" color="info" />
              <Typography variant="subtitle2">About Data Retention</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              • Retention is enforced per-organization. Each org can have different retention periods.
              <br />
              • Automated cleanup runs daily at 2:30 AM UTC, deleting data older than the configured retention period.
              <br />
              • ClickHouse tables have a 365-day global TTL as a safety net; per-org retention takes effect sooner.
              <br />
              • Disabling retention for a component will skip cleanup for that data type.
              <br />
              • Use the cleanup button to trigger an immediate cleanup for any component.
              <br />
              • The "Last Cleanup" column shows when the last automated or manual cleanup ran and how many rows were deleted.
            </Typography>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<CleanupIcon />}
              onClick={() => {
                setCleanupComponent('ALL');
                setCleanupDialogOpen(true);
              }}
              disabled={Object.values(cleaningUp).some(v => v)}
            >
              Run All Cleanups
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Cleanup Confirmation Dialog */}
      <Dialog
        open={cleanupDialogOpen}
        onClose={() => setCleanupDialogOpen(false)}
      >
        <DialogTitle>
          {cleanupComponent === 'ALL' ? 'Run All Cleanups' : `Cleanup ${cleanupComponent}`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cleanupComponent === 'ALL'
              ? 'This will run cleanup for all components. Data older than the configured retention period will be permanently deleted. This action cannot be undone.'
              : `This will delete ${cleanupComponent} data older than the configured retention period. This action cannot be undone.`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={cleanupComponent === 'ALL' ? handleCleanupAll : handleCleanupConfirm}
            color="warning"
            variant="contained"
          >
            Confirm Cleanup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RetentionSettingsPage;
